import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

function findMissingNumbers(presentSet, expectedMax) {
  if (!expectedMax || expectedMax < 1) return [];
  const missing = [];
  for (let n = 1; n <= expectedMax; n += 1) {
    if (!presentSet.has(n)) missing.push(n);
  }
  return missing;
}

function formatNumberList(nums, limit = 40) {
  if (!nums.length) return '없음';
  if (nums.length <= limit) return nums.join(', ');
  return `${nums.slice(0, limit).join(', ')} … 외 ${nums.length - limit}개`;
}

/**
 * 입력 상태 검증
 * - 장: 1..std_chapter 중 누락
 * - 절: 각 장에서 1..max(해당 장 절번호) 사이 누락 + 총 절수 vs std_verse
 */
function validateBookInput(verseRows, stdChapter, stdVerse) {
  const byChapter = new Map();
  verseRows.forEach((row) => {
    const ch = Number(row.chapter_number);
    const vs = Number(row.verse_number);
    if (!Number.isFinite(ch) || !Number.isFinite(vs)) return;
    if (!byChapter.has(ch)) byChapter.set(ch, new Set());
    byChapter.get(ch).add(vs);
  });

  const actualChapters = byChapter.size;
  const actualVerses = verseRows.length;
  const chapterSet = new Set(byChapter.keys());
  const expectedChapters = stdChapter && stdChapter > 0 ? stdChapter : Math.max(0, ...chapterSet, 0);

  const missingChapters = findMissingNumbers(chapterSet, expectedChapters);
  const extraChapters = [...chapterSet]
    .filter((ch) => expectedChapters > 0 && ch > expectedChapters)
    .sort((a, b) => a - b);

  const missingVerses = [];
  [...byChapter.entries()]
    .sort((a, b) => a[0] - b[0])
    .forEach(([chapter, verseSet]) => {
      const maxInChapter = Math.max(0, ...verseSet);
      const gaps = findMissingNumbers(verseSet, maxInChapter);
      gaps.forEach((verse) => {
        missingVerses.push({ chapter, verse });
      });
    });

  return {
    actualChapters,
    actualVerses,
    stdChapter: stdChapter ?? null,
    stdVerse: stdVerse ?? null,
    missingChapters,
    extraChapters,
    missingVerses,
    chaptersPresent: [...chapterSet].sort((a, b) => a - b),
  };
}

const BibleDetail = () => {
  const navigate = useNavigate();
  const { bookCode: rawBookCode } = useParams();
  const bookCode = decodeURIComponent(rawBookCode || '');
  const [searchParams, setSearchParams] = useSearchParams();
  const versionFromQuery = searchParams.get('version') || 'NKRV';

  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(versionFromQuery);
  const [book, setBook] = useState(null);
  const [allVerses, setAllVerses] = useState([]);
  const [validation, setValidation] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setSelectedVersion(versionFromQuery);
  }, [versionFromQuery]);

  const runLoadAndValidate = useCallback(async () => {
    if (!bookCode) return;
    setLoading(true);
    setValidating(true);
    setError('');
    setValidation(null);
    setSelectedChapter(null);

    try {
      const [{ data: versionRows, error: verErr }, { data: bookRow, error: bookErr }] =
        await Promise.all([
          supabase
            .from('bible_versions')
            .select('code,name,language_code,is_active')
            .eq('is_active', true)
            .order('code', { ascending: true }),
          supabase
            .from('bible_books')
            .select(
              'code,display_name,testament,sort_order,std_chapter,std_verse,bible_book_aliases(alias_name,language_code,is_primary)'
            )
            .eq('code', bookCode)
            .maybeSingle(),
        ]);

      if (verErr) throw verErr;
      if (bookErr) throw bookErr;
      if (!bookRow) throw new Error(`책을 찾을 수 없습니다: ${bookCode}`);

      setVersions(versionRows || []);
      setBook(bookRow);

      const versionCode = selectedVersion || versionFromQuery || 'NKRV';

      // 전체 절 로드 (검증 + 장 목록)
      const pageSize = 1000;
      let from = 0;
      const collected = [];
      for (;;) {
        const { data, error: verseErr } = await supabase
          .from('bible_version_verses')
          .select('id,chapter_number,verse_number,verse_text')
          .eq('version_code', versionCode)
          .eq('book_code', bookCode)
          .order('chapter_number', { ascending: true })
          .order('verse_number', { ascending: true })
          .range(from, from + pageSize - 1);
        if (verseErr) throw verseErr;
        const batch = data || [];
        collected.push(...batch);
        if (batch.length < pageSize) break;
        from += pageSize;
      }

      setAllVerses(collected);
      const result = validateBookInput(
        collected,
        bookRow.std_chapter,
        bookRow.std_verse
      );
      setValidation(result);

      const firstChapter = result.chaptersPresent[0] ?? null;
      setSelectedChapter(firstChapter);
    } catch (e) {
      console.error(e);
      setError(e.message || '상세 정보를 불러오지 못했습니다.');
      setAllVerses([]);
      setValidation(null);
    } finally {
      setLoading(false);
      setValidating(false);
    }
  }, [bookCode, selectedVersion, versionFromQuery]);

  useEffect(() => {
    runLoadAndValidate();
  }, [runLoadAndValidate]);

  const chapterButtons = useMemo(() => {
    if (!validation) return [];
    const std = validation.stdChapter;
    if (std && std > 0) {
      return Array.from({ length: std }, (_, i) => i + 1);
    }
    return validation.chaptersPresent;
  }, [validation]);

  const chapterVerses = useMemo(() => {
    if (!selectedChapter) return [];
    return allVerses
      .filter((v) => Number(v.chapter_number) === Number(selectedChapter))
      .sort((a, b) => a.verse_number - b.verse_number);
  }, [allVerses, selectedChapter]);

  const bookTitle = useMemo(() => {
    if (!book) return bookCode;
    const aliases = book.bible_book_aliases || [];
    const ko = aliases.find((a) => String(a.language_code || '').toLowerCase() === 'ko');
    return ko?.alias_name || book.display_name || bookCode;
  }, [book, bookCode]);

  const chapterHasData = useCallback(
    (ch) => allVerses.some((v) => Number(v.chapter_number) === Number(ch)),
    [allVerses]
  );

  const handleVersionChange = (code) => {
    setSelectedVersion(code);
    setSearchParams({ version: code });
  };

  const missingVersePreview = useMemo(() => {
    if (!validation?.missingVerses?.length) return '없음';
    const sample = validation.missingVerses
      .slice(0, 50)
      .map((m) => `${m.chapter}:${m.verse}`);
    const more =
      validation.missingVerses.length > 50
        ? ` … 외 ${validation.missingVerses.length - 50}개`
        : '';
    return `${sample.join(', ')}${more}`;
  }, [validation]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 8 }}>
        <div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mr-2"
            onClick={() => navigate('/bible')}
          >
            ← 목록
          </button>
          <span className="h5 mb-0 font-weight-bold text-primary">
            {bookTitle} <span className="text-muted">({bookCode})</span>
          </span>
        </div>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <select
            className="form-control form-control-sm"
            style={{ width: 200 }}
            value={selectedVersion}
            onChange={(e) => handleVersionChange(e.target.value)}
            disabled={loading}
          >
            {versions.map((v) => (
              <option key={v.code} value={v.code}>
                {v.name} ({v.code})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={runLoadAndValidate}
            disabled={loading || validating}
          >
            {validating ? '검증 중…' : '다시 검증'}
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {/* 1. 입력상태 확인 */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">입력 상태 확인</h6>
        </div>
        <div className="card-body">
          {loading || validating ? (
            <div className="text-muted">입력 상태를 검증하는 중…</div>
          ) : !validation ? (
            <div className="text-muted">검증 결과가 없습니다.</div>
          ) : (
            <div className="row">
              <div className="col-md-4 mb-3">
                <div className="border rounded p-3 h-100">
                  <div className="small text-muted mb-1">장수</div>
                  <div className="h4 mb-0">
                    {validation.actualChapters}
                    <span className="text-muted h6"> / {validation.stdChapter ?? '-'}</span>
                  </div>
                  <div className="small mt-1">
                    실제 카운트 / 책의 총 장수 (std_chapter)
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="border rounded p-3 h-100">
                  <div className="small text-muted mb-1">절수</div>
                  <div className="h4 mb-0">
                    {validation.actualVerses.toLocaleString()}
                    <span className="text-muted h6">
                      {' '}
                      /{' '}
                      {validation.stdVerse != null
                        ? validation.stdVerse.toLocaleString()
                        : '-'}
                    </span>
                  </div>
                  <div className="small mt-1">
                    실제 카운트 / 책의 총 절수 (std_verse)
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="border rounded p-3 h-100">
                  <div className="small text-muted mb-1">상태</div>
                  <div className="h5 mb-0">
                    {validation.missingChapters.length === 0 &&
                    validation.missingVerses.length === 0 &&
                    validation.actualChapters === (validation.stdChapter || validation.actualChapters) &&
                    validation.actualVerses === (validation.stdVerse || validation.actualVerses) ? (
                      <span className="text-success">정상</span>
                    ) : (
                      <span className="text-warning">누락/불일치</span>
                    )}
                  </div>
                  <div className="small mt-1">버전: {selectedVersion}</div>
                </div>
              </div>

              <div className="col-12">
                <div className="border rounded p-3">
                  <div className="mb-2">
                    <strong>누락된 장</strong>
                    <div className="small text-muted mt-1">
                      {formatNumberList(validation.missingChapters)}
                    </div>
                  </div>
                  {validation.extraChapters.length > 0 ? (
                    <div className="mb-2">
                      <strong>표준 초과 장</strong>
                      <div className="small text-muted mt-1">
                        {formatNumberList(validation.extraChapters)}
                      </div>
                    </div>
                  ) : null}
                  <div>
                    <strong>누락된 절</strong>
                    <div className="small text-muted mt-1" style={{ wordBreak: 'break-word' }}>
                      {missingVersePreview}
                    </div>
                    <div className="small text-muted mt-1">
                      ※ 각 장에서 1부터 해당 장 최대 절번호 사이 빈 번호를 검사합니다.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 2. 상세 (bible_reading 유사: 장 버튼 → 절 본문) */}
      <div className="card shadow mb-4">
        <div className="card-header py-3 d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-primary">상세 내역</h6>
          <span className="small text-muted">
            {bookTitle} · {selectedChapter ? `${selectedChapter}장` : '장 선택'}
          </span>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-muted">불러오는 중…</div>
          ) : (
            <>
              <div className="mb-3">
                <div className="small font-weight-bold mb-2">장 선택</div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(48px, 1fr))',
                    gap: 8,
                  }}
                >
                  {chapterButtons.map((ch) => {
                    const hasData = chapterHasData(ch);
                    const active = Number(selectedChapter) === Number(ch);
                    return (
                      <button
                        key={`ch-${ch}`}
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setSelectedChapter(ch)}
                        style={{
                          minHeight: 42,
                          fontWeight: 700,
                          border: active ? '2px solid #4e73df' : '1px solid #d1d3e2',
                          background: active ? '#eaecf4' : hasData ? '#fff' : '#f8f9fc',
                          color: hasData ? '#5a5c69' : '#b7b9cc',
                        }}
                        title={hasData ? `${ch}장` : `${ch}장 (데이터 없음)`}
                      >
                        {ch}
                      </button>
                    );
                  })}
                </div>
              </div>

              <hr />

              <div>
                <h5 className="font-weight-bold mb-3">
                  {bookTitle} {selectedChapter || '-'}장
                </h5>
                {!selectedChapter ? (
                  <p className="text-muted mb-0">장을 선택하세요.</p>
                ) : chapterVerses.length === 0 ? (
                  <p className="text-muted mb-0">이 장에 입력된 절이 없습니다.</p>
                ) : (
                  <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {chapterVerses.map((v) => (
                      <p
                        key={v.id}
                        style={{
                          lineHeight: 1.9,
                          marginBottom: '0.9rem',
                          fontSize: '1.05rem',
                        }}
                      >
                        <span className="font-weight-bold mr-2 text-primary">
                          {v.verse_number}
                        </span>
                        <span>{v.verse_text}</span>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BibleDetail;
